import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { MapPin, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useEffect } from 'react';
import { trackBeginCheckout } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

export const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getTotalAmount, clearCart } = useCart();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (cart.length > 0) {
      trackBeginCheckout(cart, getTotalAmount());
    }
  }, []); // fire once on mount

  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    phone: '',
    address: '',
    city: 'Hyderabad',
    pincode: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Pre-flight validations with user-friendly errors
    if (!RAZORPAY_KEY_ID) {
      console.error('[Checkout] REACT_APP_RAZORPAY_KEY_ID is not configured in the frontend build');
      toast.error('Payment is not configured', {
        description: 'Please contact support at farmfreshlily@gmail.com.',
      });
      return;
    }

    if (typeof window.Razorpay === 'undefined') {
      console.error('[Checkout] Razorpay script (checkout.js) has not loaded');
      toast.error('Payment service unavailable', {
        description: 'Please refresh the page and try again.',
      });
      return;
    }

    setLoading(true);

    let razorpayOrder;
    try {
      const totalAmount = getTotalAmount();
      const razorpayOrderResponse = await axios.post(`${API}/razorpay/create-order`, {
        amount: totalAmount * 100,
        currency: 'INR',
      });
      razorpayOrder = razorpayOrderResponse.data;

      if (!razorpayOrder?.id) {
        throw new Error('Razorpay order response missing id');
      }
    } catch (error) {
      console.error('[Checkout] Failed to create Razorpay order:', error?.response?.data || error?.message || error);
      toast.error('Could not start payment', {
        description: 'Please try again in a moment. If the issue persists, contact support.',
      });
      setLoading(false);
      return;
    }

    const totalAmount = getTotalAmount();
    const options = {
      key: RAZORPAY_KEY_ID,
      amount: razorpayOrder.amount,
      currency: razorpayOrder.currency,
      name: 'FarmFreshLily',
      description: 'Premium Oriental Lilies',
      order_id: razorpayOrder.id,
      handler: async function (response) {
        try {
          await axios.post(`${API}/razorpay/verify`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });

          const orderData = {
            ...formData,
            items: cart.map(item => ({
              productId: item.productId,
              quantity: item.quantity,
              orderType: item.orderType,
              price: item.price,
            })),
            totalAmount,
            paymentId: response.razorpay_payment_id,
            razorpayOrderId: response.razorpay_order_id,
          };

          const orderResponse = await axios.post(`${API}/orders`, orderData);
          navigate(`/confirmation?orderId=${orderResponse.data.id}`, { state: { order: orderResponse.data }, replace: true });
          clearCart();
        } catch (error) {
          console.error('[Checkout] Payment verification / order save failed:', error?.response?.data || error);
          toast.error('Payment received but order save failed', {
            description: 'Please contact support with your payment reference.',
          });
        }
      },
      prefill: {
        name: formData.customerName,
        email: formData.email,
        contact: formData.phone,
      },
      notes: {
        address: `${formData.address}, ${formData.city} ${formData.pincode}`,
      },
      theme: {
        color: '#C9A96E',
      },
      modal: {
        ondismiss: () => setLoading(false),
      },
    };

    try {
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (resp) => {
        console.error('[Checkout] Razorpay payment.failed:', resp?.error);
        toast.error('Payment failed', {
          description: resp?.error?.description || 'Please try a different payment method.',
        });
        setLoading(false);
      });
      razorpay.open();
    } catch (error) {
      console.error('[Checkout] Failed to open Razorpay checkout:', error);
      toast.error('Could not open payment window', {
        description: 'Please refresh the page and try again.',
      });
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-brand-cream pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-center">
          <p className="font-body text-brand-green-dark mb-4">Your cart is empty.</p>
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="bg-brand-green text-brand-cream px-8 py-3 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-gold hover:text-brand-green-dark smooth-transition"
          >
            Shop Lilies
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream pt-24 pb-16 px-6 fade-in" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-5xl font-light tracking-tight text-brand-green-dark mb-4">
            Checkout
          </h1>
          <p className="font-body text-brand-green-dark/70">
            Complete your order and enjoy A-grade lilies delivered within 2 business days
          </p>
        </div>

        <div className="bg-brand-green text-brand-cream p-6 rounded-sm mb-8 flex items-start gap-4">
          <MapPin className="w-5 h-5 text-brand-gold flex-shrink-0 mt-1" strokeWidth={1.5} />
          <div>
            <p className="font-body font-bold mb-1">
              Delivery Scope: Hyderabad Only
            </p>
            <p className="font-body text-sm text-brand-cream/85">
              We currently deliver across Hyderabad. Orders placed before 2 PM are delivered within 2 business days.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-brand-blush/20 gold-border-glow p-8 rounded-sm">
              <h2 className="font-heading text-2xl font-light text-brand-green-dark mb-6">
                Delivery Information
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block font-body text-sm text-brand-green-dark mb-2" htmlFor="customerName">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-brand-gold/40 bg-brand-cream rounded-sm focus:outline-none focus:border-brand-gold smooth-transition font-body text-brand-green-dark"
                    data-testid="input-name"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body text-sm text-brand-green-dark mb-2" htmlFor="email">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-brand-gold/40 bg-brand-cream rounded-sm focus:outline-none focus:border-brand-gold smooth-transition font-body text-brand-green-dark"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <label className="block font-body text-sm text-brand-green-dark mb-2" htmlFor="phone">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-brand-gold/40 bg-brand-cream rounded-sm focus:outline-none focus:border-brand-gold smooth-transition font-body text-brand-green-dark"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-body text-sm text-brand-green-dark mb-2" htmlFor="address">
                    Delivery Address *
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    rows="3"
                    className="w-full px-4 py-3 border border-brand-gold/40 bg-brand-cream rounded-sm focus:outline-none focus:border-brand-gold smooth-transition font-body text-brand-green-dark"
                    data-testid="input-address"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body text-sm text-brand-green-dark mb-2" htmlFor="city">
                      City *
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      readOnly
                      className="w-full px-4 py-3 border border-brand-gold/40 bg-brand-cream/60 rounded-sm font-body text-brand-green-dark cursor-not-allowed"
                      data-testid="input-city"
                    />
                  </div>

                  <div>
                    <label className="block font-body text-sm text-brand-green-dark mb-2" htmlFor="pincode">
                      Pincode *
                    </label>
                    <input
                      type="text"
                      id="pincode"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{6}"
                      className="w-full px-4 py-3 border border-brand-gold/40 bg-brand-cream rounded-sm focus:outline-none focus:border-brand-gold smooth-transition font-body text-brand-green-dark"
                      data-testid="input-pincode"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-brand-green p-8 rounded-sm sticky top-24">
              <h2 className="font-heading text-2xl font-light text-brand-cream mb-6">
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                {cart.map((item) => (
                  <div key={`${item.productId}-${item.orderType}`} className="flex justify-between font-body text-sm text-brand-cream/95">
                    <span>
                      {item.name} × {item.quantity}
                    </span>
                    <span>₹{item.price * item.quantity}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-brand-gold/40 pt-4 mb-6">
                <div className="flex justify-between font-body text-brand-cream/90 mb-2">
                  <span>Subtotal</span>
                  <span>₹{getTotalAmount()}</span>
                </div>
                <div className="flex justify-between font-body text-brand-cream/90 mb-2">
                  <span>Delivery</span>
                  <span className="text-brand-gold text-sm">Free</span>
                </div>
              </div>

              <div className="border-t border-brand-gold/40 pt-4 mb-6">
                <div className="flex justify-between font-heading text-2xl text-brand-cream">
                  <span>Total</span>
                  <span data-testid="checkout-total">₹{getTotalAmount()}</span>
                </div>
              </div>

              <div className="bg-brand-gold/20 p-4 rounded-sm mb-4 flex items-start gap-3">
                <Clock className="w-4 h-4 text-brand-gold flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="font-body text-xs text-brand-cream">
                  Delivered within 2 business days for orders placed before 2 PM
                </p>
              </div>

              <div className="bg-brand-gold/20 p-4 rounded-sm mb-6 flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-brand-gold flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="font-body text-xs text-brand-cream">
                  A-Grade lilies with beautiful aroma, ready for a beautiful vase or evening
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-gold text-brand-green-dark py-4 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-cream smooth-transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="place-order-btn"
              >
                {loading ? 'Processing...' : 'Place Order'}
              </button>

              <p className="font-body text-xs text-brand-cream/70 text-center mt-4">
                Secure payment powered by Razorpay
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
